<template>
  <!--BOOST LANDING PAGE: Story CD-18 -->
  <div class="my-4 px-4 grid grid-cols-3">
    <div class="col-span-1">
      <div class="flex items-center space-x-2" v-if="isDesktop">
        <p class="text-sm font-medium text-grey-light-600">Rows per page</p>
        <Select :model-value="`${pageSize}`" @update:model-value="$emit('update:pageSize', Number($event))">
          <SelectTrigger class="h-8 w-[70px]">
            <SelectValue :placeholder="`${pageSize}`" />
          </SelectTrigger>
          <SelectContent side="top">
            <SelectItem v-for="size in pageSizeOptions" :key="size" :value="`${size}`">
              {{ size }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div class="col-span-1 flex justify-center">
      <Pagination v-slot="{ page }" :total="totalCount" :itemsPerPage="pageSize" :page="currentPage" :default-page="1">
        <PaginationList v-slot="{ items }" class="flex items-center gap-1">
          <PaginationFirst :disabled="currentPage === 1" @click="currentPage > 1 && $emit('update:page', 1)" />
          <PaginationPrev
            :disabled="currentPage === 1"
            @click="currentPage > 1 && $emit('update:page', currentPage - 1)"
          />

          <template v-for="(item, index) in items">
            <PaginationListItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button
                class="w-10 h-10 p-0"
                @click="$emit('update:page', item.value)"
                :variant="item.value === page ? 'primary' : 'outline'"
              >
                {{ item.value }}
              </Button>
            </PaginationListItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>

          <PaginationNext
            :disabled="currentPage >= totalPages"
            @click="currentPage < totalPages && $emit('update:page', currentPage + 1)"
          />
          <PaginationLast
            :disabled="currentPage >= totalPages"
            @click="currentPage < totalPages && $emit('update:page', totalPages)"
          />
        </PaginationList>
      </Pagination>
    </div>

    <div class="col-span-1 flex justify-end" v-if="isDesktop">
      <div class="flex items-center justify-center text-sm font-medium text-grey-light-600">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import {
  Pagination,
  PaginationEllipsis,
  PaginationFirst,
  PaginationLast,
  PaginationList,
  PaginationListItem,
  PaginationNext,
  PaginationPrev,
} from '@/components/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { computed } from 'vue';

interface Props {
  totalCount: number;
  currentPage: number;
  pageSize: number;
  pageSizeOptions: readonly number[];
}

const props = defineProps<Props>();
const { isDesktop } = useBreakpoint();

defineEmits<{
  'update:page': [page: number];
  'update:pageSize': [size: number];
}>();

const totalPages = computed(() => {
  return Math.ceil(props.totalCount / props.pageSize);
});
</script>
